import { writable } from "svelte/store"
import { querystring } from "svelte-spa-router"
import { queryStringToJSON } from "../../../../Script/queryString"

// Global variable to store parsed query parameters
let query

// Subscribe to changes in the querystring and update the `query` variable accordingly
querystring.subscribe((state) => (query = queryStringToJSON(state)))

/**
 * Represents a document object.
 * @class
 */
export class SinglePublishObject {
  /**
   * Constructs a new SinglePublishObject instance.
   * @constructor
   */
  constructor() {
    /**
     * Indicates the active component.
     * @type {number}
     * @default 0
     */
    this.active = 0

    /**
     * denotes the selected document template id.
     * @type {undefined | string}
     * @default undefined
     */
    this.documentTemplateId = undefined

    /**
     * QR code for the URL generated in the server and return as a base64 image
     * @type {undefined | string}
     * @default undefined
     */
    this.QRCode = undefined

    /**
     * blockChain URL
     * @type {undefined | string}
     * @default undefined
     */
    this.proposedURL = undefined

    /**
     * denotes the document id from initial api.
     * @type {undefined | string}
     * @default undefined
     */
    this.documentId = undefined

    /**
     * It contains the metadata object.
     * @type {object}
     * @default {}
     */
    this.Json = {}

    /**
     * It contains the additional fields to be included in metadata.
     * @type {Array}
     * @default {}
     */
    this.fields = []

    /**
     * It contains the template details.
     * @type {object}
     * @default {}
     */
    this.template = {}

    /**
     * The PDF URI (Uniform Resource Identifier) of the document.
     * @type {undefined | string}
     * @default undefined
     */
    this.uri = undefined

    /**
     * uploaded PDF file.
     * @type {File}
     * @default ""
     */
    this.uri = ""

    /**
     * Signed PDF name.
     * @type {undefined | string}
     * @default undefined
     */
    this.signedPDFName = undefined

    /**
     * id denotes pdf is signed or not.
     * @type {Boolean}
     * @default false
     */
    this.signedPDF = false

    /**
     * dataHash.
     * @type {undefined | string}
     * @default undefined
     */
    this.dataHash = undefined

    /**
     * signing token.
     * @type {undefined | String}
     * @default undefined
     */
    this.token = undefined

    /**
     * signing token.
     * @type {String}
     * @default undefined
     */
    this.txid = undefined

    /**
     * signing token.
     * @type {String}
     * @default undefined
     */
    this.signature = undefined

    /**
     * fileHash.
     * @type {undefined | string}
     * @default undefined
     */
    this.fileHash = undefined

    /**
     * selected document template id.
     * @type {undefined | string}
     * @default undefined
     */
    this.templateID = undefined
  }
}

/**
 * Represents an array of SinglePublishObject instances.
 * @typedef {Array<SinglePublishObject>} SinglePublishArray
 */

/**
 * Retrieves the single publish data from localStorage.
 * @type {SinglePublishArray} - The array of single publish data objects retrieved from local storage.
 */
let stats = localStorage.getItem("P2BSinglePublish")
let data = {}
if (stats == null) {
  try {
    // Parsing the value as JSON
    stats = JSON.parse(stats)
    if (!stats) {
      stats = []
      // Throwing an error if the parsed value is null
      throw new Error("Document value is null")
    }
  } catch (error) {
    // console.error(error)
    // Initializing stats with a new instance of the SinglePublishObject class
    stats = [...stats, new SinglePublishObject()]
  }
} else {
  stats = JSON.parse(stats)
  for (let i = 0; i < stats.length; i++) {
    if (stats[i].documentId == query.documentId) {
      data = stats[i]
    }
  }
}

/**
 * @typedef {Array<SinglePublish>} SinglePublishArray - single publisg stats Array.
 * @type {Array}
 * single publisg stats array.
 */

/**
 * @typedef {Object} SinglePublish - single publishing stats.
 * @type {Object}
 * single publishing stats.
 * 
 * @property {String} documentId - denotes the document id from initial api.
 * @property {String} QRCode - QR code for the URL generated in the server and return as a base64 image
 * @property {String} proposedURL - blockChain URL
 * @property {String} documentTemplateId - denotes the selected document template id.
 * @property {Object} Json - It contains the metadata object.
 * @property {Array} fields - It contains additional fields to be included in metadata.
 * @property {String} optionsTitle - it contain title and expiry date of the published documnet.
 * @property {String} expiryOn - Date value.
 * @property {String} uri - The PDF URI (Uniform Resource Identifier) of the document.
 * @property {String} signedPDFName - Signed PDF name.
 * @property {String} signedPDF - Signed PDF name.
 * @property {String} dataHash - dataHash.
 * @property {String} fileHash - fileHash.
 * @property {File} pdfFile - signed or uploaded pdf file.
 * @property {File | "pdf" | "image"} fileType - uploaded file type pdf or image
 * @property {String} token - signing token.
 * @property {String} txid - signature transaction id.
 * /
 
/**
 * @type {import("svelte/store").Writable<SinglePublish>}
 */

const SinglePublish = writable(data)

/**
 * Updates the value of "P2BSinglePublish" in the localStorage.
 * @param {object} stats - The stats object to be stored.
 */
export const updateSinglePublish = async (stat) => {
  try {
    for (let i = 0; i < stats.length; i++) {
      if (stats[i].documentId == stat.documentId) {
        stats[i] = stat
        throw "new enter added"
      }
    }
    stats = [...stats, stat]
  } catch {
  } finally {
    localStorage.setItem("P2BSinglePublish", JSON.stringify(stats))
  }
}

/**
 * Updates the value of "P2BSinglePublish" in the localStorage.
 * @param {object} stats - The stats object to be stored.
 */
export const removeDataSinglePublish = async (id) => {
  try {
    stats = stats.filter((stat) => stat.documentId != id)
  } catch {
  } finally {
    localStorage.setItem("P2BSinglePublish", JSON.stringify(stats))
  }
}
export default SinglePublish
